// 📂 src/pages/CameraDetail/components/PlaybackControls.jsx
// Enterprise playback control bar — compact, keyboard-driven, Milestone-grade

import { useRef, useState, useCallback } from "react";
import {
  FiPlay,
  FiPause,
  FiSkipBack,
  FiSkipForward,
  FiChevronsLeft,
  FiChevronsRight,
  FiRewind,
  FiFastForward,
} from "react-icons/fi";

const SPEEDS = [0.25, 0.5, 1, 2, 4, 8, 16];

function formatTimecode(seconds) {
  if (!seconds || isNaN(seconds)) return "0:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function PlaybackControls({
  playing,
  onPlayPause,
  playbackSpeed,
  onSpeedChange,
  onFrameStep,
  onJump,
  currentTime = 0,
  duration = 0,
  disabled = false,
}) {
  const scrubberRef = useRef(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubPosition, setScrubPosition] = useState(null);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const displayProgress = isScrubbing && scrubPosition != null ? scrubPosition : progress;

  /* ─── Inline scrubber ─── */
  const getScrubPercent = useCallback((e) => {
    const rect = scrubberRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
  }, []);

  const handleScrubStart = useCallback((e) => {
    if (disabled) return;
    setIsScrubbing(true);
    const pct = getScrubPercent(e);
    setScrubPosition(pct);
  }, [disabled, getScrubPercent]);

  const handleScrubMove = useCallback((e) => {
    if (!isScrubbing) return;
    setScrubPosition(getScrubPercent(e));
  }, [isScrubbing, getScrubPercent]);

  const handleScrubEnd = useCallback(() => {
    if (isScrubbing && scrubPosition != null && duration > 0 && onJump) {
      const targetTime = (scrubPosition / 100) * duration;
      const delta = targetTime - currentTime;
      onJump(delta);
    }
    setIsScrubbing(false);
    setScrubPosition(null);
  }, [isScrubbing, scrubPosition, duration, currentTime, onJump]);

  /* ─── Touch support ─── */
  const handleTouchStart = useCallback((e) => {
    if (disabled) return;
    const touch = e.touches[0];
    const rect = scrubberRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100));
    setIsScrubbing(true);
    setScrubPosition(pct);
  }, [disabled]);

  const handleTouchMove = useCallback((e) => {
    if (!isScrubbing) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = scrubberRef.current?.getBoundingClientRect();
    if (!rect) return;
    setScrubPosition(Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100)));
  }, [isScrubbing]);

  const speedIdx = SPEEDS.indexOf(playbackSpeed);

  return (
    <div className="bg-[#111] border-t border-white/5">
      {/* Progress scrubber bar */}
      <div
        ref={scrubberRef}
        className="relative h-6 cursor-pointer group"
        onMouseDown={handleScrubStart}
        onMouseMove={handleScrubMove}
        onMouseUp={handleScrubEnd}
        onMouseLeave={handleScrubEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleScrubEnd}
      >
        {/* Track */}
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-white/8 rounded-full group-hover:h-1.5 transition-all">
          {/* Buffered / loaded */}
          <div
            className="absolute inset-y-0 left-0 bg-white/10 rounded-full"
            style={{ width: `${Math.min(displayProgress + 5, 100)}%` }}
          />
          {/* Progress */}
          <div
            className="absolute inset-y-0 left-0 bg-blue-500 rounded-full transition-[width] duration-75"
            style={{ width: `${displayProgress}%` }}
          />
        </div>
        {/* Scrub handle */}
        <div
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full bg-white shadow-lg shadow-black/30 transition-all ${
            isScrubbing
              ? "w-3.5 h-3.5 scale-110"
              : "w-2.5 h-2.5 opacity-0 group-hover:opacity-100"
          }`}
          style={{ left: `${displayProgress}%` }}
        />
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-between px-3 py-1.5">
        {/* Left: transport controls */}
        <div className="flex items-center gap-0.5">
          {/* Jump back 10s */}
          <button
            onClick={() => onJump?.(-10)}
            disabled={disabled}
            className="pc-btn"
            title="Jump −10s (Shift+←)"
          >
            <FiChevronsLeft className="w-4 h-4" />
          </button>

          {/* Frame back */}
          <button
            onClick={() => onFrameStep?.(-1)}
            disabled={disabled}
            className="pc-btn"
            title="Previous frame (←)"
          >
            <FiSkipBack className="w-3.5 h-3.5" />
          </button>

          {/* Play / Pause */}
          <button
            onClick={onPlayPause}
            disabled={disabled}
            className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all ${
              disabled
                ? "opacity-30 cursor-not-allowed"
                : playing
                  ? "bg-white text-black hover:bg-gray-200"
                  : "bg-blue-600 text-white hover:bg-blue-500"
            }`}
            title={playing ? "Pause (Space)" : "Play (Space)"}
          >
            {playing ? <FiPause className="w-4 h-4" /> : <FiPlay className="w-4 h-4 ml-0.5" />}
          </button>

          {/* Frame forward */}
          <button
            onClick={() => onFrameStep?.(1)}
            disabled={disabled}
            className="pc-btn"
            title="Next frame (→)"
          >
            <FiSkipForward className="w-3.5 h-3.5" />
          </button>

          {/* Jump forward 10s */}
          <button
            onClick={() => onJump?.(10)}
            disabled={disabled}
            className="pc-btn"
            title="Jump +10s (Shift+→)"
          >
            <FiChevronsRight className="w-4 h-4" />
          </button>
        </div>

        {/* Center: timecode */}
        <div className="flex items-center gap-1.5 text-xs font-mono tabular-nums select-none">
          <span className="text-white">{formatTimecode(currentTime)}</span>
          <span className="text-gray-600">/</span>
          <span className="text-gray-500">{formatTimecode(duration)}</span>
        </div>

        {/* Right: speed pills */}
        <div className="flex items-center gap-0.5">
          {/* Slow down */}
          <button
            onClick={() => speedIdx > 0 && onSpeedChange?.(SPEEDS[speedIdx - 1])}
            disabled={disabled || speedIdx <= 0}
            className="pc-btn"
            title="Slower (J / ↓)"
          >
            <FiRewind className="w-3 h-3" />
          </button>

          {/* Speed display */}
          <button
            onClick={() => onSpeedChange?.(1)}
            disabled={disabled}
            className={`px-2 py-1 text-[11px] font-mono font-medium rounded transition-colors ${
              playbackSpeed === 1
                ? "bg-white/10 text-white"
                : "text-yellow-400 bg-yellow-500/10 border border-yellow-500/20"
            }`}
            title="Reset to 1× speed"
          >
            {playbackSpeed}×
          </button>

          {/* Speed up */}
          <button
            onClick={() => speedIdx < SPEEDS.length - 1 && onSpeedChange?.(SPEEDS[speedIdx + 1])}
            disabled={disabled || speedIdx >= SPEEDS.length - 1}
            className="pc-btn"
            title="Faster (L / ↑)"
          >
            <FiFastForward className="w-3 h-3" />
          </button>
        </div>
      </div>

      <style>{`
        .pc-btn {
          display: flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 6px;
          color: rgba(255,255,255,0.6); background: transparent; border: none;
          cursor: pointer; transition: all 0.15s;
        }
        .pc-btn:hover { color: white; background: rgba(255,255,255,0.08); }
        .pc-btn:disabled { opacity: 0.25; cursor: not-allowed; }
        .pc-btn:disabled:hover { background: transparent; color: rgba(255,255,255,0.6); }
      `}</style>
    </div>
  );
}
